import { get, post } from '@rover/react-lib/apis/address.api';
import addressFactory from '@rover/react-lib/factories/addressFactory';
import type {
  ActionCreator,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/lib/redux-duck';
import { createDuck } from '@rover/react-lib/lib/redux-duck';
import type { Address } from '@rover/react-lib/types/Address';
import triggerEmbeddedEvent from '@rover/utilities/embedded';

export type State = {
  address: Address;
  errorMessage: string;
  successMessage: string;
  loading: boolean;
  submitting: boolean;
};

type Actions = {
  fetchAddressPending: ActionCreator<void>;
  fetchAddressSuccess: ActionCreator<{ address: Address }>;
  fetchAddressFailure: ActionCreator<{ message: string }>;
  saveAddressPending: ActionCreator<void>;
  saveAddressSuccess: ActionCreator<{ address: Address }>;
  saveAddressFailure: ActionCreator<{ message: string }>;
  resetAddress: ActionCreator<Partial<Address>>;
};

type Effects = {
  fetchAddress: EffectCreator<string>;
  saveAddress: EffectCreator<{ personOpk: string; address: Address }>;
};

type Selectors = {
  getAddress: SelectorCreator<Address>;
  getAccessToHome: SelectorCreator<string>;
  getZip: SelectorCreator<string>;
  isAddressSubmitting: SelectorCreator<boolean>;
  getErrorMessage: SelectorCreator<string>;
  getLoading: SelectorCreator<boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'address',
  initialState: {
    address: addressFactory(),
    errorMessage: '',
    successMessage: '',
    loading: false,
    submitting: false,
  },
  actions: {
    fetchAddressPending: () => (state) => ({
      ...state,
      loading: true,
    }),
    fetchAddressSuccess: ({ address }) => (state) => ({
      ...state,
      loading: false,
      address,
      errorMessage: '',
    }),
    fetchAddressFailure: ({ message }) => (state) => ({
      ...state,
      loading: false,
      errorMessage: message,
    }),
    saveAddressPending: () => (state) => ({
      ...state,
      submitting: true,
    }),
    saveAddressSuccess: ({ address }) => (state) => ({
      ...state,
      submitting: false,
      address,
      errorMessage: '',
    }),
    saveAddressFailure: ({ message }) => (state) => ({
      ...state,
      submitting: false,
      errorMessage: message,
    }),
    resetAddress: (opts) => (state) => ({
      ...state,
      address: addressFactory(opts),
    }),
  },
  effects: {
    fetchAddress: (personOpk: string) => async (
      dispatch,
      getState,
      duckActions,
      duckEffects,
      duckSelectors,
      { expressParams }
    ) => {
      dispatch(duckActions.fetchAddressPending());
      try {
        const address = await get(personOpk, expressParams);
        dispatch(duckActions.fetchAddressSuccess({ address }));
      } catch (error) {
        dispatch(duckActions.fetchAddressFailure(error));
      }
    },
    saveAddress: ({ personOpk, address }) => async (
      dispatch,
      getState,
      duckActions,
      duckEffects,
      duckSelectors,
      { expressParams }
    ): Promise<boolean> => {
      dispatch(duckActions.saveAddressPending());
      try {
        await post(personOpk, address, expressParams);
        dispatch(duckActions.saveAddressSuccess({ address }));
        triggerEmbeddedEvent('address-modified');
      } catch (error) {
        dispatch(duckActions.saveAddressFailure(error));
        return false;
      }
      return true;
    },
  },
  selectors: {
    getAddress: (getState, createSelector) => createSelector([getState], (s) => s.address),
    getAccessToHome: (getState, createSelector, duckSelectors) =>
      createSelector([duckSelectors.getAddress], (address) => address.accessToHome),
    getZip: (getState, createSelector, duckSelectors) =>
      createSelector([duckSelectors.getAddress], (address) => address.zip),
    isAddressSubmitting: (getState, createSelector) =>
      createSelector([getState], (s) => s.submitting),
    getErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.errorMessage),
    getLoading: (getState, createSelector) => createSelector([getState], (s) => s.loading),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options);



// WEBPACK FOOTER //
// ../react-lib/src/redux/ducks/address.duck.ts